// Period-over-period math for Meta trends. Months are "YYYY-MM" keys.
const toIdx = (key) => { const [y, m] = key.split("-").map(Number); return y * 12 + (m - 1); };
const toKey = (i) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const monthLabel = (key, withYear = false) => {
  const [y, m] = key.split("-");
  return withYear ? `${MONTHS[+m - 1]} ${y}` : MONTHS[+m - 1];
};
export const rangeLabel = (keys) => (keys.length ? (keys.length === 1 ? monthLabel(keys[0], true) : `${monthLabel(keys[0])} to ${monthLabel(keys[keys.length - 1], true)}`) : "");

const sum = (rows) => {
  const t = rows.reduce((s, m) => ({
    impressions: s.impressions + m.impressions, clicks: s.clicks + m.clicks,
    back_impressions: s.back_impressions + (m.back_impressions || 0), view_through: s.view_through + (m.view_through || 0),
  }), { impressions: 0, clicks: 0, back_impressions: 0, view_through: 0 });
  return { ...t, ctr: t.impressions > 0 ? Number(((t.clicks / t.impressions) * 100).toFixed(2)) : 0, count: rows.length };
};

const window_ = (byKey, startIdx, n) => {
  const keys = Array.from({ length: n }, (_, i) => toKey(startIdx + i));
  return { keys, rows: keys.map((k) => byKey[k]).filter(Boolean) };
};

export const buildTrends = (months, reportStart, reportEnd) => {
  if (!months.length) return null;
  const byKey = Object.fromEntries(months.map((m) => [m.month, m]));
  const last = months[months.length - 1].month;
  const endIdx = toIdx((reportEnd || "").slice(0, 7) || last);
  const startIdx = reportStart ? toIdx(reportStart.slice(0, 7)) : endIdx;
  const n = Math.max(1, endIdx - startIdx + 1);
  const cur = window_(byKey, startIdx, n);
  if (!cur.rows.length) return null;
  const prior = window_(byKey, startIdx - n, n);
  const yoy = window_(byKey, startIdx - 12, n);
  const chartStart = Math.max(startIdx - n, endIdx - 11);
  const chartAll = window_(byKey, chartStart, endIdx - chartStart + 1).keys.map((k) => ({
    key: k, label: monthLabel(k), current: toIdx(k) >= startIdx, ...(byKey[k] || { impressions: 0, clicks: 0, ctr: null }),
  }));
  const firstData = chartAll.findIndex((c) => c.impressions > 0);
  const chart = firstData > 0 ? chartAll.slice(firstData) : chartAll;
  const best = cur.rows.reduce((b, m) => (!b || m.clicks > b.clicks ? m : b), null);
  return {
    n, cur: { ...sum(cur.rows), keys: cur.keys.filter((k) => byKey[k]) },
    prior: prior.rows.length ? { ...sum(prior.rows), keys: prior.keys.filter((k) => byKey[k]) } : null,
    yoy: yoy.rows.length === n ? { ...sum(yoy.rows), keys: yoy.keys } : null,
    chart, best: cur.rows.length > 1 ? best : null,
    sinceStart: { ...sum(months.filter((m) => toIdx(m.month) <= endIdx)), first: months[0].month },
    freshness: adFreshness(months.filter((m) => toIdx(m.month) <= endIdx)),
  };
};

export const delta = (now, before) => {
  if (before == null || before === 0) return null;
  const pct = ((now - before) / before) * 100;
  return Math.abs(pct) < 0.5 ? { pct: 0, dir: "flat" } : { pct, dir: pct > 0 ? "up" : "down" };
};

// Ads still running in the latest month that have run 2+ months: first vs latest month click rate.
const adFreshness = (months) => {
  const runs = {};
  months.forEach((m) => (m.creatives || []).forEach((c) => {
    if (!(c.impressions > 0)) return;
    (runs[c.name] = runs[c.name] || []).push({ month: m.month, ctr: (c.clicks / c.impressions) * 100 });
  }));
  const latestMonth = months.length ? months[months.length - 1].month : "";
  return Object.entries(runs)
    .filter(([, r]) => r.length >= 2 && r[r.length - 1].month === latestMonth)
    .map(([name, r]) => {
      const first = r[0].ctr;
      const latest = r[r.length - 1].ctr;
      return { name, months: r.length, from: r[0].month, first, latest, tired: latest < first * 0.8 };
    })
    .sort((a, b) => b.months - a.months)
    .slice(0, 3);
};

// December "Year in Review": full calendar-year totals, best month, early vs late growth, prior-year comparison.
export const buildYearInReview = (months, reportEnd) => {
  if (!reportEnd || reportEnd.slice(5, 7) !== "12") return null;
  const year = reportEnd.slice(0, 4);
  const inYear = months.filter((m) => m.month.startsWith(year));
  if (inYear.length < 2) return null;
  const total = sum(inYear);
  const best = inYear.reduce((b, m) => (!b || m.clicks > b.clicks ? m : b), null);
  const span = inYear.length >= 6 ? 3 : 1;
  const early = sum(inYear.slice(0, span));
  const late = sum(inYear.slice(-span));
  const lastYear = months.filter((m) => m.month.startsWith(String(+year - 1)));
  return {
    year, total, best,
    early: { ...early, keys: inYear.slice(0, span).map((m) => m.month) },
    late: { ...late, keys: inYear.slice(-span).map((m) => m.month) },
    clicksPerMonth: { early: early.clicks / span, late: late.clicks / span },
    lastYear: lastYear.length >= inYear.length ? sum(lastYear) : null,
  };
};
